"use client";

import { useEffect } from "react";
import {
  apiOperationContext,
  captureApiOperation,
  captureClientFailure,
  captureFeatureView,
  captureEngagement,
  captureNavigation,
  capturePageLoad,
  captureInteraction,
  captureScrollDepth,
} from "@/lib/analytics/service-quality";
import { featureForPath } from "@/features/service-quality/catalog";
import { getApiBaseUrl } from "@/lib/api-client";
import { capturePostHogPageview } from "@/lib/analytics/posthog";

export function ServiceQualityTelemetry() {
  useEffect(() => {
    let lastCapturedRoute = location.href;
    capturePostHogPageview();
    captureFeatureView();
    const navigation = performance.getEntriesByType("navigation")[0] as
      PerformanceNavigationTiming | undefined;
    capturePageLoad(navigation?.duration || performance.now());
    const originalFetch = window.fetch.bind(window);
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(
      window.history,
    );
    let currentFeature =
      featureForPath(window.location.pathname)?.id ?? "unknown";
    let lastActivityAt = Date.now();
    let scrollMilestones = new Set<number>();
    // The SPA talks to the API on its own origin, so a request is ours when
    // it sits under the configured API base — not when its path starts
    // `/api/`, which nothing on this origin serves any more.
    const apiBase = new URL(
      `${getApiBaseUrl()}/`,
      window.location.origin,
    ).href.replace(/\/$/, "");
    const isApiRequest = (input: RequestInfo | URL) => {
      const href =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      return new URL(href, window.location.origin).href.startsWith(
        `${apiBase}/`,
      );
    };
    window.fetch = async (...args) => {
      const startedAt = performance.now();
      const context = apiOperationContext();
      try {
        const response = await originalFetch(...args);
        if (isApiRequest(args[0]))
          captureApiOperation(
            args[0],
            response,
            startedAt,
            args[1]?.method,
            context,
          );
        return response;
      } catch (error) {
        if (isApiRequest(args[0]))
          captureApiOperation(
            args[0],
            null,
            startedAt,
            args[1]?.method,
            context,
          );
        throw error;
      }
    };
    const captureRoutedLoad = (
      startedAt: number,
      fromFeature: string,
      fromPath: string,
    ) =>
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
          if (window.location.pathname === fromPath) return;
          if (location.href === lastCapturedRoute) return;
          lastCapturedRoute = location.href;
          const duration = performance.now() - startedAt;
          capturePostHogPageview();
          captureFeatureView();
          capturePageLoad(duration);
          captureNavigation(fromFeature, duration);
          currentFeature =
            featureForPath(window.location.pathname)?.id ?? "unknown";
          scrollMilestones = new Set<number>();
        }),
      );
    window.history.pushState = (...args) => {
      const startedAt = performance.now();
      const fromFeature = currentFeature;
      const fromPath = window.location.pathname;
      originalPushState(...args);
      captureRoutedLoad(startedAt, fromFeature, fromPath);
    };
    window.history.replaceState = (...args) => {
      const startedAt = performance.now();
      const fromFeature = currentFeature;
      const fromPath = window.location.pathname;
      originalReplaceState(...args);
      captureRoutedLoad(startedAt, fromFeature, fromPath);
    };
    const onPopState = () => {
      const startedAt = performance.now();
      const fromFeature = currentFeature;
      captureRoutedLoad(startedAt, fromFeature, "");
    };
    const onClick = (event: MouseEvent) => {
      lastActivityAt = Date.now();
      const navigationStartedAt = performance.now();
      const navigationFromFeature = currentFeature;
      const navigationFromPath = window.location.pathname;
      const element = (event.target as Element | null)?.closest(
        "button,a,input,select,textarea",
      );
      if (element) captureInteraction(element, "activate");
      const navigationElement = (event.target as Element | null)?.closest(
        "button.nav-item,button.brand,a[href^='/']",
      );
      if (navigationElement)
        captureRoutedLoad(
          navigationStartedAt,
          navigationFromFeature,
          navigationFromPath,
        );
    };
    const onActivity = () => {
      lastActivityAt = Date.now();
    };
    const onScroll = () => {
      lastActivityAt = Date.now();
      const available =
        document.documentElement.scrollHeight - window.innerHeight;
      if (available <= 0) return;
      const depth = (window.scrollY / available) * 100;
      for (const milestone of [25, 50, 75, 100]) {
        if (depth >= milestone && !scrollMilestones.has(milestone)) {
          scrollMilestones.add(milestone);
          captureScrollDepth(milestone);
        }
      }
    };
    const onSubmit = (event: SubmitEvent) =>
      captureInteraction(event.target as Element, "submit");
    const onError = () => captureClientFailure("uncaught_error");
    const onRejection = () => captureClientFailure("unhandled_rejection");
    window.addEventListener("popstate", onPopState);
    document.addEventListener("click", onClick, true);
    document.addEventListener("submit", onSubmit, true);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("keydown", onActivity, true);
    window.addEventListener("pointermove", onActivity, true);
    window.addEventListener("scroll", onScroll, { passive: true });
    const heartbeat = window.setInterval(() => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastActivityAt <= 60_000
      )
        captureEngagement(15);
    }, 15_000);
    return () => {
      window.fetch = originalFetch;
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("keydown", onActivity, true);
      window.removeEventListener("pointermove", onActivity, true);
      window.removeEventListener("scroll", onScroll);
      window.clearInterval(heartbeat);
    };
  }, []);
  return null;
}
